/**
 * Which tunnel an OU (level > 1) hybrid tunnel actually stands on — the
 * vertical half of the hybrid layout, in raw inches.
 *
 * A hybrid level is a chain of tunnels along X, each with its own length and
 * its own `x_offset`, so "the level below" is not one tunnel: a 24" tunnel
 * standing at X 220 on a level whose tunnels run 0–184 and 184–289 sits on the
 * SECOND of them, and takes its floor from that one's roof. Picking the lead
 * (`position = 1`) tunnel instead — which is all the canvas did, as a
 * documented simplification — lifts it onto whatever the lead's height happens
 * to be, which is why AHU 3985 drew its top plenum a level too high and called
 * the unit 204" tall when it is 166" (reported 2026-10-08).
 *
 * This is a port of `ahu/tunnel_geometry.py::_resolve_z`, which is what the
 * website's own layout (`resolve_ahu_layout`) and the Model View
 * (`cad_generator.py`'s Z pass, itself a copy of the same rule) place the
 * tunnel from. Ported rather than reused for the reason `hybridXOffset` and
 * `hybridYOffset` are ports: the canvas has no access to the backend resolver
 * and needs the answer in its own draw loop. **Keep the three in step** — a
 * canvas that disagrees with `resolve_ahu_layout` disagrees with Model View,
 * the callout schedule and the CAD model all at once.
 *
 * The rule, from the bottom up:
 *
 * 1. Level 1 stands on the ground: z = 0, and its height includes its base.
 * 2. For each higher level, a tunnel's support is the tunnel below whose X
 *    span its own UPSTREAM edge falls in — one candidate per row, preferring a
 *    tunnel that starts exactly at that edge, then one that merely contains
 *    it, then the nearest within a 1" tolerance.
 * 3. Several rows can answer; the ones that also overlap this tunnel in depth
 *    win, and of those the LOWEST roof, so a tunnel bridging two rows rests on
 *    both rather than hanging off the taller one.
 * 4. A tunnel with no support under its upstream edge, or none overlapping it
 *    in depth, is "floating" — it keeps the lowest roof available, and stops
 *    counting as support for anything above it.
 */
import { getTunnelPhysicalSpan, type TunnelSpanFields } from './hybridXOffset';
import { getTunnelCenterline, type TunnelYFields } from './hybridYOffset';

/** Deliberately structural, matching `TunnelSpanFields` — see that comment. */
export interface TunnelStackFields extends TunnelSpanFields, TunnelYFields {
  height?: number | string | null;
  /** `tunnel.get('base')?.height || 0` — the build-up under the tunnel. */
  baseHeight?: number | string | null;
}

export interface TunnelStackPlacement {
  /** Height of this tunnel's floor above the AHU's own, inches. */
  zOrigin: number;
  /** Height of its roof — `zOrigin` + its height + its base. */
  top: number;
  /** The tunnel it stands on, null for level 1 and for a unit it can't find one on. */
  supportId: number | null;
  /** Nothing below its upstream edge, or nothing below it in depth. */
  floating: boolean;
}

/** `tunnel_geometry.TOLERANCE` — how far off an edge may be and still land. */
const TOLERANCE = 1.0;
/** `tunnel_geometry.EPS`, the same value, so "starts exactly here" means the
 *  same thing on both sides of the port. */
const EPS = 1e-6;

const num = (v: number | string | null | undefined): number => parseFloat(String(v ?? 0)) || 0;

const overallHeight = (t: TunnelStackFields): number => num(t.height) + num(t.baseHeight);

interface Span {
  left: number;
  right: number;
}

/** `_row_x_candidate` — the tunnel of one row an edge comes down on. */
function rowXCandidate<T extends TunnelStackFields>(
  edge: number,
  rowTunnels: T[],
  spans: Map<number, Span>
): T | undefined {
  const spanOf = (t: T) => spans.get(t.id) as Span;
  const starter = rowTunnels.find((t) => Math.abs(spanOf(t).left - edge) < EPS);
  if (starter) return starter;
  const exact = rowTunnels.find((t) => spanOf(t).left <= edge && edge <= spanOf(t).right);
  if (exact) return exact;
  const near = rowTunnels.filter(
    (t) => spanOf(t).left - TOLERANCE <= edge && edge <= spanOf(t).right + TOLERANCE
  );
  if (!near.length) return undefined;
  const distance = (t: T) =>
    Math.min(Math.abs(spanOf(t).left - edge), Math.abs(spanOf(t).right - edge));
  return near.reduce((best, t) => (distance(t) < distance(best) ? t : best));
}

/**
 * Every tunnel's floor and roof height, keyed by tunnel id.
 *
 * Levels resolve strictly bottom-up and nothing looks upward, so one forward
 * pass is enough — a support is always already placed by the time anything
 * stands on it.
 */
export function resolveTunnelStack<T extends TunnelStackFields>(
  tunnels: T[]
): Map<number, TunnelStackPlacement> {
  const placements = new Map<number, TunnelStackPlacement>();
  const spans = new Map<number, Span>();
  const depths = new Map<number, Span>();
  const byLevel = new Map<number, T[]>();
  tunnels.forEach((t) => {
    spans.set(t.id, getTunnelPhysicalSpan(t));
    const centre = getTunnelCenterline(t, tunnels);
    const width = num(t.width);
    depths.set(t.id, { left: centre - width / 2, right: centre + width / 2 });
    const level = t.level || 1;
    const atLevel = byLevel.get(level);
    if (atLevel) atLevel.push(t);
    else byLevel.set(level, [t]);
  });

  (byLevel.get(1) || []).forEach((t) => {
    placements.set(t.id, {
      zOrigin: 0,
      top: overallHeight(t),
      supportId: null,
      floating: false,
    });
  });

  const depthOverlap = (a: T, b: T, tolerance: number): boolean => {
    const da = depths.get(a.id) as Span;
    const db = depths.get(b.id) as Span;
    return da.right >= db.left - tolerance && da.left <= db.right + tolerance;
  };
  const roofOf = (t: T): number => placements.get(t.id)?.top ?? 0;
  const lowestRoof = (candidates: T[]): T | undefined =>
    candidates.length
      ? candidates.reduce((best, t) => (roofOf(t) < roofOf(best) ? t : best))
      : undefined;

  const higherLevels = [...byLevel.keys()].filter((l) => l > 1).sort((a, b) => a - b);
  higherLevels.forEach((level) => {
    const below = (byLevel.get(level - 1) || []).filter((t) => placements.has(t.id));
    const standing = below.filter((t) => !placements.get(t.id)?.floating);
    const byRow = new Map<number, T[]>();
    standing.forEach((t) => {
      const row = t.row || 1;
      const inRow = byRow.get(row);
      if (inRow) inRow.push(t);
      else byRow.set(row, [t]);
    });

    (byLevel.get(level) || []).forEach((tunnel) => {
      const span = spans.get(tunnel.id) as Span;
      const isRtl = Number(tunnel.airflow_direction_id) === 2;
      const upstreamEdge = isRtl ? span.right : span.left;
      const xMatches = [...byRow.values()]
        .map((rowTunnels) => rowXCandidate(upstreamEdge, rowTunnels, spans))
        .filter((t): t is T => t !== undefined);
      const floatingX = !xMatches.length;
      const floatingY = !standing.some((t) => depthOverlap(tunnel, t, TOLERANCE));
      const floating = floatingX || floatingY;

      let support: T | undefined;
      if (floating) support = lowestRoof(standing.length ? standing : below);
      else if (xMatches.length === 1) support = xMatches[0];
      else {
        const overlapping = xMatches.filter((t) => depthOverlap(tunnel, t, 0));
        support = lowestRoof(overlapping.length ? overlapping : xMatches);
      }

      const zOrigin = support ? roofOf(support) : 0;
      placements.set(tunnel.id, {
        zOrigin,
        top: zOrigin + overallHeight(tunnel),
        supportId: support ? support.id : null,
        floating,
      });
    });
  });

  return placements;
}

/**
 * How tall the stack actually stands, inches — the highest roof on the unit.
 *
 * Not the same as adding up one height per level: levels are chains, and the
 * tunnel that starts a level need not be the one a higher level stands on.
 */
export function stackedAhuHeight<T extends TunnelStackFields>(tunnels: T[]): number {
  if (!tunnels.length) return 0;
  const placements = resolveTunnelStack(tunnels);
  return Math.max(0, ...[...placements.values()].map((p) => p.top));
}
